import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import { Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { create } from "superstruct";
import { injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import PurchasedProductListResponseEntity from "../entities/PurchasedProductListResponseEntity";
import PurchasedProductListResponseSchema from "../schemas/PurchasedProductListResponseSchema";
import PurchasedProductsStore from "../stores/PurchasedProductsStore";

interface GetPurchasedProductsRequestParamsDTO {
    reviewRequestToken?: string;
    excludedProductId?: number;
    offset?: number;
    orderId?: number;
}

@injectable()
class PurchasedProductsService {
    constructor(
        private purchasedProductsStore: PurchasedProductsStore,
        private readonly apiClient: ApiClientInterface,
        private readonly logger: LoggerInterface
    ) {}
    private getPurchasedProductsRequest(
        reqParams: GetPurchasedProductsRequestParamsDTO
    ): Observable<PurchasedProductListResponseEntity> {
        this.logger.debug(
            "PurchasedProductsService.getPurchasedProductsByProductId is started",
            reqParams
        );
        const currentStatus = this.purchasedProductsStore.getStatus();
        this.purchasedProductsStore.setStatus(
            currentStatus === STATUS.VIRGIN
                ? STATUS.INITIAL_LOADING
                : STATUS.LOADING
        );

        return this.apiClient
            .get({
                url: "/review/getPurchasedProducts",
                responseType: "json",
                queryParams: {
                    ...reqParams,
                },
            })
            .pipe(
                finalize(() => {
                    this.purchasedProductsStore.setLoading(false);
                }),
                map((v) => create(v.body, PurchasedProductListResponseSchema)),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "PurchasedProductsService.getPurchasedProductsByProductId is errored",
                        { reqParams, error }
                    );
                    applyTransaction(() => {
                        this.purchasedProductsStore.setError(
                            (error as Error).message
                        );
                        this.purchasedProductsStore.setStatus(STATUS.ERROR);
                    });
                    throw error;
                }),
                tap(() => {
                    this.logger.debug(
                        "PurchasedProductsService.getPurchasedProductsByProductId is ready",
                        reqParams
                    );
                })
            );
    }

    getPurchasedProducts(
        reqParams: GetPurchasedProductsRequestParamsDTO
    ): Observable<PurchasedProductListResponseEntity> {
        return this.getPurchasedProductsRequest(reqParams).pipe(
            tap((v) => {
                applyTransaction(() => {
                    this.purchasedProductsStore.set(v);
                    this.purchasedProductsStore.setStatus(STATUS.READY);
                });
            })
        );
    }
}

export default PurchasedProductsService;
